import React from 'react';

const Experience: React.FC = () => {
  const experiences = [
    {
      title: 'Technical Support Advisor II',
      company: 'Concentrix',
      period: 'October 2025 – Present',
      location: 'Hamilton, ON',
      description: [
        'Provide technical support for a leading global tech client covering both hardware and software',
        'Troubleshoot device-related issues via phone and chat channels',
        'Ensure high customer satisfaction through efficient problem resolution',
        'Handle complex technical escalations and provide advanced troubleshooting support'
      ],
      technologies: ['Technical Support', 'Hardware Troubleshooting', 'Software Support', 'Customer Service'],
      icon: 'fas fa-headphones-alt'
    },
    {
      title: 'Technical Analyst',
      company: 'IT Infocom',
      period: 'Apr 2025 – Oct 2025',
      location: 'Hybrid/India',
      description: [
        'Installed and supported security software systems including CCTV and biometric devices',
        'Assisted in setting up file servers, email systems, and antivirus solutions for clients',
        'Provided technical support for network infrastructure and security implementations',
        'Collaborated with clients to ensure proper setup and configuration of IT systems'
      ],
      technologies: ['Security Systems', 'CCTV', 'Biometric Devices', 'File Servers', 'Email Systems', 'Antivirus'],
      icon: 'fas fa-shield-alt'
    },
    {
      title: 'Technical Support Analyst',
      company: 'Tecsys Inc.',
      period: 'May 2022 – Mar 2024',
      location: 'Markham, ON',
      description: [
        'Troubleshot C# code for OMS; improved system performance',
        'Handled MS SQL data updates and ensured integrity',
        'Resolved 10–20 client cases/day within SLAs',
        'Reduced downtime 15% via monitoring tools',
        'REST and SOAP APIs for system integration and troubleshooting using Postman and Soap UI'
      ],
      technologies: ['C#', 'MS SQL Server', 'DataDog', 'New Relic', 'REST API', 'SOAP API', 'Postman', 'Soap UI'],
      icon: 'fas fa-code'
    },
    {
      title: 'Digital Offline Customer Service Representative – Tier 2',
      company: 'NTT BPO',
      period: 'May 2021 – May 2022',
      location: 'Toronto, ON',
      description: [
        'Resolved 40+ daily orders, shipments and other issues',
        'Provided Tier 2 support using Salesforce',
        'Trained 10+ hires; improved onboarding speed',
        'Handled 40+ incoming customer inquiries daily'
      ],
      technologies: ['Salesforce', 'Citrix', 'Customer Service', 'Training', 'Technical Support'],
      icon: 'fas fa-users'
    }
  ];

  return (
    <section id="experience" className="py-20 bg-slate-900/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 fade-in">
            My <span className="gradient-text">Experience</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto fade-in">
            A journey through my professional experience and key achievements
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          {/* Timeline Line */}
          <div className="absolute left-8 md:left-1/2 transform md:-translate-x-1/2 h-full w-1 bg-gradient-to-b from-primary via-secondary to-accent"></div>

          {/* Experience Items */}
          <div className="space-y-12">
            {experiences.map((exp, index) => (
              <div
                key={index}
                className={`timeline-item relative flex items-center ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Timeline Marker */}
                <div className="absolute left-[1.875rem] md:left-1/2 transform md:-translate-x-1/2 w-6 h-6 bg-primary rounded-full border-4 border-dark z-10 shadow-lg shadow-primary/50"></div>

                {/* Experience Card */}
                <div className={`experience-card w-full md:w-[calc(50%-3rem)] ml-16 md:ml-0 ${
                  index % 2 === 0 ? 'md:mr-auto md:pr-12' : 'md:ml-auto md:pl-12'
                }`}>
                  <div className="bg-slate-800/50 backdrop-blur-sm p-6 md:p-8 rounded-xl border border-slate-700/50 hover:bg-slate-800/70 hover:border-primary/50 transition-all duration-300">
                    {/* Header */}
                    <div className="flex items-start mb-4">
                      <div className="w-14 h-14 bg-primary/20 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                        <i className={`${exp.icon} text-primary text-2xl`}></i>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-1">{exp.title}</h3>
                        <p className="text-primary font-semibold text-base">{exp.company}</p>
                      </div>
                    </div>

                    {/* Period and Location */}
                    <div className="flex flex-wrap items-center gap-3 mb-4 text-sm">
                      <span className="bg-primary/20 text-primary px-3 py-1.5 rounded-full flex items-center">
                        <i className="fas fa-calendar-alt mr-2"></i>
                        {exp.period}
                      </span>
                      <span className="bg-secondary/20 text-secondary px-3 py-1.5 rounded-full flex items-center">
                        <i className="fas fa-map-marker-alt mr-2"></i>
                        {exp.location}
                      </span>
                    </div>

                    {/* Description */}
                    <ul className="space-y-2.5 mb-6 text-gray-300">
                      {exp.description.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start">
                          <i className="fas fa-check-circle text-primary mr-3 mt-1 text-sm flex-shrink-0"></i>
                          <span className="text-sm leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Technologies */}
                    <div>
                      <h4 className="text-white font-semibold mb-3 text-sm flex items-center">
                        <i className="fas fa-code text-primary mr-2"></i>
                        Technologies Used:
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {exp.technologies.map((tech, techIndex) => (
                          <span
                            key={techIndex}
                            className="tech-tag bg-slate-700/50 text-gray-300 px-3 py-1 rounded-full text-xs font-medium border border-slate-600/50 hover:bg-primary/20 hover:border-primary/50 hover:text-primary transition-all duration-300"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-16 text-center fade-in">
          <div className="bg-slate-800/30 backdrop-blur-sm p-8 rounded-xl border border-slate-700/50">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center justify-center">
              <i className="fas fa-trophy text-primary mr-2"></i>
              Key Achievements
            </h3>
            <div className="grid md:grid-cols-3 gap-8 mt-8">
              <div className="text-center">
                <div className="text-4xl font-bold text-primary mb-2">1000+</div>
                <div className="text-gray-400 font-medium">Issues Resolved</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-primary mb-2">4+</div>
                <div className="text-gray-400 font-medium">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-primary mb-2">20+</div>
                <div className="text-gray-400 font-medium">Projects Delivered</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;